"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SpeedResultSchema = exports.SPEED_COOLDOWN_MINUTES = exports.SPEED_FRESH_DAYS = exports.SpeedMeasurementSchema = exports.SPEED_METRIC_KEYS = exports.SPEED_METRICS = exports.SpeedGradeSchema = exports.SpeedStrategySchema = void 0;
exports.gradeOf = gradeOf;
exports.gradeOfScore = gradeOfScore;
exports.speedIsFresh = speedIsFresh;
const zod_1 = require("zod");
/**
 * Đo tốc độ thật qua PageSpeed Insights — spec SC-18.
 *
 * VÌ SAO CÓ FILE NÀY: mục kiểm tra `performance` trước đây chấm bằng thời gian
 * máy chủ trả byte đầu tiên, đo từ chính container API, rồi gắn nhãn
 * "Performance". Một trang React nặng 4 MB mất 8 giây mới hiện chữ vẫn được chấm
 * "ok — máy chủ trả về sau 180ms". Nhãn nói một đằng, con số đo một nẻo.
 */
exports.SpeedStrategySchema = zod_1.z.enum(['mobile', 'desktop']);
/** Tốt / Cần cải thiện / Kém — đúng ba mức của Google, không tự chế thêm. */
exports.SpeedGradeSchema = zod_1.z.enum(['tot', 'trung-binh', 'kem']);
/**
 * Ngưỡng lấy theo tài liệu web.dev của Google, không tự đặt.
 *
 * `giaiThich` là phần bắt buộc: người dùng của Copilot làm marketing. "CLS 0,31"
 * không nói gì; "bố cục nhảy khi trang đang tải, người đọc bấm nhầm nút" thì nói
 * được, và đó mới là thứ họ mang đi nói chuyện với đội kỹ thuật.
 */
exports.SPEED_METRICS = {
    lcpMs: {
        label: 'Nội dung chính hiện ra',
        viet: 'LCP',
        tot: 2500,
        trungBinh: 4000,
        donVi: 'ms',
        giaiThich: 'Bao lâu thì phần nội dung lớn nhất — thường là ảnh bìa hoặc tiêu đề — hiện xong.',
    },
    clsX1000: {
        label: 'Bố cục xô lệch',
        viet: 'CLS',
        tot: 100,
        trungBinh: 250,
        donVi: '',
        giaiThich: 'Trang có bị nhảy chỗ trong lúc tải không. Nhảy nhiều thì người đọc bấm nhầm nút.',
    },
    tbtMs: {
        label: 'Thời gian trang bị đơ',
        viet: 'TBT',
        tot: 200,
        trungBinh: 600,
        donVi: 'ms',
        giaiThich: 'Khoảng thời gian trang đã hiện nhưng bấm vào chưa ăn, vì JavaScript đang chạy.',
    },
    fcpMs: {
        label: 'Chữ đầu tiên hiện ra',
        viet: 'FCP',
        tot: 1800,
        trungBinh: 3000,
        donVi: 'ms',
        giaiThich: 'Bao lâu thì người dùng thấy được thứ gì đó thay vì màn hình trắng.',
    },
};
exports.SPEED_METRIC_KEYS = Object.keys(exports.SPEED_METRICS);
function gradeOf(key, value) {
    const m = exports.SPEED_METRICS[key];
    if (value <= m.tot)
        return 'tot';
    if (value <= m.trungBinh)
        return 'trung-binh';
    return 'kem';
}
/** Điểm Lighthouse: mốc 90 và 50 là mốc chính Google dùng để tô xanh/vàng/đỏ. */
function gradeOfScore(perfScore) {
    if (perfScore >= 90)
        return 'tot';
    if (perfScore >= 50)
        return 'trung-binh';
    return 'kem';
}
exports.SpeedMeasurementSchema = zod_1.z.object({
    strategy: exports.SpeedStrategySchema,
    /** Điểm Performance của Lighthouse, 0–100 */
    perfScore: zod_1.z.number().int().min(0).max(100),
    lcpMs: zod_1.z.number().int(),
    /** CLS × 1000, số nguyên — xem lý do ở comment cột `clsX1000` trong schema.prisma */
    clsX1000: zod_1.z.number().int(),
    tbtMs: zod_1.z.number().int(),
    fcpMs: zod_1.z.number().int(),
    siMs: zod_1.z.number().int(),
    measuredAt: zod_1.z.coerce.date(),
});
/**
 * Số đo cũ hơn ngần này ngày thì KHÔNG dùng để chấm điểm nữa — spec SC-18 §5.3.
 *
 * Một trang thay đổi liên tục thì số đo hai tuần trước không còn mô tả đúng nó,
 * mà chấm điểm theo số cũ là quay lại đúng lỗi spec này đang sửa.
 */
exports.SPEED_FRESH_DAYS = 7;
function speedIsFresh(measuredAt, now = new Date()) {
    // NHẬN CẢ CHUỖI, có chủ đích. Tầng API gọi hàm này với `Date` thật lấy từ Prisma,
    // còn giao diện gọi nó với thứ vừa qua `res.json()` — mà `apps/admin/src/api.ts`
    // KHÔNG parse lại bằng zod, chỉ ép kiểu. Nên `measuredAt` ở trình duyệt là một
    // CHUỖI ISO đội lốt `Date`, và `measuredAt.getTime()` ném lỗi làm trắng cả màn.
    //
    // TypeScript không bắt được kiểu lỗi này vì hợp đồng khai là `Date` và không ai
    // nói dối ở tầng kiểu — chỉ có thực tế lúc chạy khác đi. Đây cũng là lý do
    // `format.ts` của giao diện dùng kiểu `DateLike` cho mọi hàm định dạng ngày.
    const moc = measuredAt instanceof Date ? measuredAt : new Date(measuredAt);
    return now.getTime() - moc.getTime() <= exports.SPEED_FRESH_DAYS * 24 * 60 * 60 * 1000;
}
/**
 * Một trang chỉ đo lại được sau ngần này phút — spec SC-18 §4 Y7.
 * Đây là lời gọi ra dịch vụ ngoài có hạn mức chung cho cả hệ thống; không chặn
 * thì một người bấm liên tục là hết hạn mức của mọi người.
 */
exports.SPEED_COOLDOWN_MINUTES = 10;
/** Kết quả trả về cho giao diện: số đo mới nhất + có phải vừa gọi Google không. */
exports.SpeedResultSchema = zod_1.z.object({
    measurement: exports.SpeedMeasurementSchema.nullable(),
    /** false = trả lại số đo đã lưu vì còn trong thời gian chờ giữa hai lượt đo */
    vuaDo: zod_1.z.boolean(),
    /** Còn bao nhiêu phút nữa mới đo lại được; 0 khi đo được ngay */
    choThemPhut: zod_1.z.number().int().min(0),
});
//# sourceMappingURL=speed.js.map